<script >
import '@/features/charting-next/components/workspace/chartingWorkspace.css';
import '@/features/charting-next/compat/chartingUtilities.css';
import ChartingFeedbackLayer from '@/features/charting-next/compat/ChartingFeedbackLayer.vue';
import ChartingMobileBottomBar from '@/features/charting-next/components/mobile/ChartingMobileBottomBar.vue';
import ChartingMobileChartDialog from '@/features/charting-next/components/mobile/ChartingMobileChartDialog.vue';
import ChartingMobileDetailDialog from '@/features/charting-next/components/mobile/ChartingMobileDetailDialog.vue';
import ChartingMobileFavoriteSheet from '@/features/charting-next/components/mobile/ChartingMobileFavoriteSheet.vue';
import ChartingMobileFeed from '@/features/charting-next/components/mobile/ChartingMobileFeed.vue';
import ChartingMobileToolbar from '@/features/charting-next/components/mobile/ChartingMobileToolbar.vue';
import ChartingWorkspaceResultsColumn from '@/features/charting-next/components/workspace/ChartingWorkspaceResultsColumn.vue';
import ChartingWorkspaceSearchDialog from '@/features/charting-next/components/workspace/ChartingWorkspaceSearchDialog.vue';
import ChartingWorkspaceSidebar from '@/features/charting-next/components/workspace/ChartingWorkspaceSidebar.vue';
import ChartingWorkspaceStage from '@/features/charting-next/components/workspace/ChartingWorkspaceStage.vue';
import ChartingWorkspaceTableStage from '@/features/charting-next/components/workspace/ChartingWorkspaceTableStage.vue';
import ChartingWorkspaceWorkspacesDialog from '@/features/charting-next/components/workspace/ChartingWorkspaceWorkspacesDialog.vue';
import { useChartingFinderControls } from '@/features/charting-next/composables/useChartingFinderControls';
import { useChartingViewport } from '@/features/charting-next/composables/useChartingViewport';
import { useChartingWorkspace } from '@/features/charting-next/composables/useChartingWorkspace';
import { computed, ref, watch } from '@vue/composition-api';
import { useChartingRoute as useRoute, useChartingRouter as useRouter } from '@/features/charting-next/services/chartingRouter';
import { useChartingToast as useToast } from '@/features/charting-next/services/chartingFeedback';
const __sfc_main = {};
__sfc_main.setup = (__props, __ctx) => {
  const route = useRoute();
  const router = useRouter();
  const toast = useToast();
  const viewport = useChartingViewport();
  const workspace = useChartingWorkspace(route, router, {
    enableSelectedOhlc: computed(() => !viewport.isMobile.value)
  });
  const finderControls = useChartingFinderControls(workspace);
  const searchDialogVisible = ref(false);
  const workspacesDialogVisible = ref(false);
  const activeInspectorPanel = ref('');
  const mobileChartVisible = ref(false);
  const mobileChartSeries = ref(null);
  const mobileDetailVisible = ref(false);
  const mobileFavoriteVisible = ref(false);
  const mobileActiveTicker = ref('');
  const mobileFallbackStock = ref(null);
  const inspectorColumnWidth = computed(() => activeInspectorPanel.value ? '34rem' : '4.75rem');
  const canSaveCurrentSearch = computed(() => Boolean(workspace.marketExplorer.isActive.value && finderControls.isReady.value && workspace.capabilities.value.canManageSavedSearches));
  const explorerLabel = computed(() => workspace.marketExplorer.summary.value.count ? `Explorar · ${workspace.marketExplorer.summary.value.count} filtros` : 'Explorar · Sin filtros');
  const marketPageError = computed(() => workspace.marketExplorer.isActive.value ? workspace.marketExplorer.state.value?.error || workspace.collectionResource.error.value : null);
  const mobileActiveStock = computed(() => workspace.results.value.find(stock => stock.ticker === mobileActiveTicker.value) || mobileFallbackStock.value);
  const mobileFeedResetKey = computed(() => {
    const appliedQuery = workspace.marketExplorer.state.value?.appliedQuery;
    const normalizedQuery = appliedQuery ? {
      ...appliedQuery,
      pagination: {
        ...(appliedQuery.pagination || {}),
        offset: 0
      }
    } : null;
    return `${workspace.entry.value.signature}:${JSON.stringify(normalizedQuery)}`;
  });
  const runMutationFeedback = async ({
    summary,
    successDetail,
    errorDetail,
    operation
  }) => {
    try {
      await operation();
      toast.add({
        severity: 'success',
        summary,
        detail: successDetail,
        life: 3000
      });
    } catch (error) {
      toast.add({
        severity: 'error',
        summary,
        detail: error.message || errorDetail,
        life: 3000
      });
    }
  };
  const handleCreateFavoriteFolder = payload => runMutationFeedback({
    summary: 'Favoritos',
    successDetail: 'La carpeta de favoritos se ha creado.',
    errorDetail: 'No se pudo crear la carpeta.',
    operation: () => workspace.createFavoriteFolder(payload.name, payload.color)
  });
  const handleRenameFavoriteFolder = payload => runMutationFeedback({
    summary: 'Favoritos',
    successDetail: 'La carpeta de favoritos se ha actualizado.',
    errorDetail: 'No se pudo actualizar la carpeta.',
    operation: () => workspace.renameFavoriteFolder(payload)
  });
  const handleRemoveFavoriteFolder = name => runMutationFeedback({
    summary: 'Favoritos',
    successDetail: 'La carpeta de favoritos se ha eliminado.',
    errorDetail: 'No se pudo eliminar la carpeta.',
    operation: () => workspace.removeFavoriteFolder(name)
  });
  const handleCreateSavedSearch = ({
    name
  }) => runMutationFeedback({
    summary: 'Busquedas',
    successDetail: 'La busqueda se ha guardado.',
    errorDetail: 'No se pudo guardar la busqueda.',
    operation: () => workspace.createSavedSearch({
      name,
      definition: workspace.marketExplorer.state.value?.appliedQuery
    })
  });
  const handleRenameSavedSearch = payload => runMutationFeedback({
    summary: 'Busquedas',
    successDetail: 'La busqueda se ha actualizado.',
    errorDetail: 'No se pudo actualizar la busqueda.',
    operation: () => workspace.renameSavedSearch(payload)
  });
  const handleRemoveSavedSearch = name => runMutationFeedback({
    summary: 'Busquedas',
    successDetail: 'La busqueda se ha eliminado.',
    errorDetail: 'No se pudo eliminar la busqueda.',
    operation: () => workspace.removeSavedSearch(name)
  });
  const handleMetricMutation = operation => {
    operation().catch(error => {
      toast.add({
        severity: 'error',
        summary: 'Métricas',
        detail: error.message || 'No se pudieron actualizar las métricas.',
        life: 4000
      });
    });
  };
  const openExplorer = () => {
    finderControls.beginDraft();
    searchDialogVisible.value = true;
  };
  const handleExplorerVisibility = visible => {
    if (!visible) finderControls.cancelDraft();
    searchDialogVisible.value = visible;
  };
  const handleApplyExplorer = async () => {
    try {
      await finderControls.applyFilters();
      searchDialogVisible.value = false;
    } catch (error) {
      // The dialog keeps the draft and renders the typed mutation error.
    }
  };
  const handleOpenSavedSearch = async source => {
    finderControls.cancelDraft();
    searchDialogVisible.value = false;
    await workspace.openSource(source);
  };
  const setMobileActiveStock = stock => {
    if (!stock?.ticker) return;
    mobileActiveTicker.value = stock.ticker;
    mobileFallbackStock.value = stock;
    workspace.selectTicker(stock.ticker).catch(() => {});
  };
  const openMobileDetails = stock => {
    setMobileActiveStock(stock);
    mobileDetailVisible.value = true;
  };
  const openMobileChart = ({
    stock,
    series
  }) => {
    setMobileActiveStock(stock);
    mobileChartSeries.value = series;
    mobileChartVisible.value = true;
  };
  const openMobileFavorites = stock => {
    if (!workspace.capabilities.value.canUseFavorites) {
      toast.add({
        severity: 'warn',
        summary: 'Favoritos',
        detail: 'Tu plan actual no permite gestionar favoritos.',
        life: 3500
      });
      return;
    }
    setMobileActiveStock(stock);
    mobileFavoriteVisible.value = true;
  };
  const toggleMobileFavoriteFolder = async folderName => {
    if (!mobileActiveStock.value) return;
    await workspace.toggleFavoriteFolderForStock(mobileActiveStock.value, folderName);
  };
  const handleMobileEntryVariant = ({
    ticker,
    entryVariantKey
  }) => {
    workspace.selectEntryVariant(ticker, entryVariantKey).catch(() => {});
  };
  const handleMobileTableTicker = ticker => {
    const stock = workspace.results.value.find(item => item.ticker === ticker);
    if (stock) openMobileDetails(stock);
  };
  watch(mobileFeedResetKey, (nextKey, previousKey) => {
    if (!previousKey || nextKey === previousKey || !viewport.isMobile.value) return;
    window.requestAnimationFrame(() => window.scrollTo({
      top: 0,
      behavior: 'auto'
    }));
  });
  return {
    viewport,
    workspace,
    finderControls,
    searchDialogVisible,
    workspacesDialogVisible,
    activeInspectorPanel,
    mobileChartVisible,
    mobileChartSeries,
    mobileDetailVisible,
    mobileFavoriteVisible,
    mobileActiveTicker,
    inspectorColumnWidth,
    canSaveCurrentSearch,
    explorerLabel,
    marketPageError,
    mobileActiveStock,
    mobileFeedResetKey,
    handleCreateFavoriteFolder,
    handleRenameFavoriteFolder,
    handleRemoveFavoriteFolder,
    handleCreateSavedSearch,
    handleRenameSavedSearch,
    handleRemoveSavedSearch,
    handleMetricMutation,
    openExplorer,
    handleExplorerVisibility,
    handleApplyExplorer,
    handleOpenSavedSearch,
    openMobileDetails,
    openMobileChart,
    openMobileFavorites,
    toggleMobileFavoriteFolder,
    handleMobileEntryVariant,
    handleMobileTableTicker
  };
};
__sfc_main.components = Object.assign({
  ChartingFeedbackLayer,
  ChartingMobileToolbar,
  ChartingMobileFeed,
  ChartingWorkspaceTableStage,
  ChartingMobileBottomBar,
  ChartingWorkspaceResultsColumn,
  ChartingWorkspaceStage,
  ChartingWorkspaceSidebar,
  ChartingMobileChartDialog,
  ChartingMobileDetailDialog,
  ChartingMobileFavoriteSheet,
  ChartingWorkspaceSearchDialog,
  ChartingWorkspaceWorkspacesDialog
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <div class="charting-workspace">
        <ChartingFeedbackLayer />
        <div v-if="workspace.collectionResource.error.value" class="brand-banner-warning rounded-xl border p-4 text-sm">
            {{ workspace.collectionResource.error.value.message || 'No se pudo cargar la fuente activa del graficador.' }}
        </div>

        <div v-if="viewport.isMobile.value" class="charting-mobile-workspace">
            <ChartingMobileToolbar
                :view-mode="workspace.workspaceViewMode.value"
                :resolution="workspace.contextStore.resolution"
                :ticker-search="workspace.tickerSearch.value"
                :sort-options="workspace.resultSortOptions.value"
                :sort-value="workspace.resultSortValue.value"
                :total-results="workspace.totalResults.value"
                :loaded-results="workspace.results.value.length"
                :is-loading="workspace.collectionResource.isLoading.value"
                @change-view-mode="workspace.setWorkspaceViewMode"
                @set-resolution="workspace.setResolution"
                @update:ticker-search="workspace.tickerSearch.value = $event"
                @submit-ticker-search="workspace.submitTickerSearch"
                @change-sort="workspace.setResultSort"
            />

            <ChartingMobileFeed
                v-show="workspace.workspaceViewMode.value === 'chart'"
                :enabled="workspace.workspaceViewMode.value === 'chart'"
                :results="workspace.results.value"
                :resolution="workspace.contextStore.resolution"
                :is-loading="workspace.collectionResource.isLoading.value"
                :empty-message="(workspace.collectionResource.data.value && workspace.collectionResource.data.value.emptyMessage) || ''"
                :has-more-results="workspace.hasMoreResults.value"
                :is-loading-next-page="workspace.isLoadingNextPage.value"
                :page-error="marketPageError"
                :can-use-favorites="workspace.capabilities.value.canUseFavorites"
                :is-favorite-mutating="workspace.isMutatingFavorites.value"
                :reset-key="mobileFeedResetKey"
                @load-more="workspace.loadNextPage"
                @retry-page="workspace.loadNextPage"
                @open-chart="openMobileChart"
                @more-information="openMobileDetails"
                @open-favorites="openMobileFavorites"
                @select-entry-variant="handleMobileEntryVariant"
            />

            <div v-show="workspace.workspaceViewMode.value === 'table'" class="charting-mobile-workspace__table">
                <ChartingWorkspaceTableStage
                    :results="workspace.results.value"
                    :selected-ticker="mobileActiveTicker"
                    :columns="workspace.chartTableColumns.value"
                    :is-loading="workspace.collectionResource.isLoading.value"
                    :empty-message="(workspace.collectionResource.data.value && workspace.collectionResource.data.value.emptyMessage) || ''"
                    :sort-value="workspace.resultSortValue.value"
                    :sort-options="workspace.resultSortOptions.value"
                    :total-results="workspace.totalResults.value"
                    :has-more-results="workspace.hasMoreResults.value"
                    :is-loading-next-page="workspace.isLoadingNextPage.value"
                    :page-error="marketPageError"
                    document-scroll-pagination
                    @select-ticker="handleMobileTableTicker"
                    @change-sort="workspace.setResultSort"
                    @select-entry-variant="handleMobileEntryVariant"
                    @load-more="workspace.loadNextPage"
                    @retry="workspace.loadNextPage"
                />
            </div>

            <ChartingMobileBottomBar
                :filters-count="workspace.marketExplorer.summary.value.count"
                @open-filters="openExplorer"
                @open-workspaces="workspacesDialogVisible = true"
            />
        </div>

        <div v-else class="charting-workspace-grid" :style="{ '--charting-workspace-inspector-width': inspectorColumnWidth }">
            <ChartingWorkspaceResultsColumn
                :results="workspace.results.value"
                :selected-ticker="(workspace.selectedStock.value && workspace.selectedStock.value.ticker) || ''"
                :selected-stock="workspace.selectedStock.value"
                :chart-series="workspace.ohlcSeriesResource.data.value"
                :is-chart-loading="workspace.ohlcSeriesResource.isLoading.value"
                :chart-error="workspace.ohlcSeriesResource.error.value"
                :resolution="workspace.contextStore.resolution"
                :view-mode="workspace.workspaceViewMode.value"
                :is-loading="workspace.collectionResource.isLoading.value"
                :empty-message="(workspace.collectionResource.data.value && workspace.collectionResource.data.value.emptyMessage) || ''"
                :sort-options="workspace.resultSortOptions.value"
                :sort-value="workspace.resultSortValue.value"
                :ticker-search="workspace.tickerSearch.value"
                :total-results="workspace.totalResults.value"
                :has-more-results="workspace.hasMoreResults.value"
                :is-loading-next-page="workspace.isLoadingNextPage.value"
                :page-error="marketPageError"
                :explore-label="explorerLabel"
                @open-search="openExplorer"
                @open-workspaces="workspacesDialogVisible = true"
                @update:ticker-search="workspace.tickerSearch.value = $event"
                @submit-ticker-search="workspace.submitTickerSearch"
                @load-more="workspace.loadNextPage"
                @retry="workspace.loadNextPage"
                @select-ticker="workspace.selectTicker"
                @change-sort="workspace.setResultSort"
                @change-view-mode="workspace.setWorkspaceViewMode"
            />

            <div class="charting-workspace-stage-column">
                <ChartingWorkspaceStage
                    v-if="workspace.workspaceViewMode.value === 'chart'"
                    :stock="workspace.selectedStock.value"
                    :chart-series="workspace.ohlcSeriesResource.data.value"
                    :is-chart-loading="workspace.ohlcSeriesResource.isLoading.value"
                    :chart-error="workspace.ohlcSeriesResource.error.value"
                    :resolution="workspace.contextStore.resolution"
                    :price-series-type="workspace.contextStore.priceSeriesType"
                    :show-volume="workspace.contextStore.showVolume"
                    :overlay-visibility="workspace.contextStore.overlayVisibility"
                    :metrics="workspace.chartMetrics.value"
                    :available-metrics="workspace.availableChartMetrics.value"
                    :is-metrics-loading="workspace.metricPreferencesResource.isLoading.value"
                    :is-metric-mutating="workspace.isMutatingChartMetrics.value"
                    :metric-error="workspace.chartMetricMutationError.value || workspace.metricPreferencesResource.error.value"
                    @select-entry-variant="workspace.selectEntryVariant(workspace.selectedStock.value && workspace.selectedStock.value.ticker, $event)"
                    @set-price-series-type="workspace.setPriceSeriesType"
                    @toggle-volume="workspace.toggleShowVolume"
                    @toggle-overlay-visibility="workspace.toggleOverlayVisibility"
                    @set-resolution="workspace.setResolution"
                    @add-metric="handleMetricMutation(() => workspace.addChartMetric($event))"
                    @remove-metric="handleMetricMutation(() => workspace.removeChartMetric($event))"
                />
                <ChartingWorkspaceTableStage
                    v-else
                    :results="workspace.results.value"
                    :selected-ticker="(workspace.selectedStock.value && workspace.selectedStock.value.ticker) || ''"
                    :columns="workspace.chartTableColumns.value"
                    :is-loading="workspace.collectionResource.isLoading.value"
                    :empty-message="(workspace.collectionResource.data.value && workspace.collectionResource.data.value.emptyMessage) || ''"
                    :sort-value="workspace.resultSortValue.value"
                    :sort-options="workspace.resultSortOptions.value"
                    :total-results="workspace.totalResults.value"
                    :has-more-results="workspace.hasMoreResults.value"
                    :is-loading-next-page="workspace.isLoadingNextPage.value"
                    :page-error="marketPageError"
                    @select-ticker="workspace.selectTicker"
                    @change-sort="workspace.setResultSort"
                    @select-entry-variant="workspace.selectEntryVariant($event.ticker, $event.entryVariantKey)"
                    @load-more="workspace.loadNextPage"
                    @retry="workspace.loadNextPage"
                />
            </div>

            <ChartingWorkspaceSidebar :stock="workspace.selectedStock.value" :active-panel="activeInspectorPanel" @update:active-panel="activeInspectorPanel = $event" />
        </div>

        <ChartingMobileChartDialog
            v-if="viewport.isMobile.value || mobileChartVisible"
            :visible="mobileChartVisible"
            :stock="mobileChartVisible ? mobileActiveStock : null"
            :series="mobileChartSeries"
            :resolution="workspace.contextStore.resolution"
            @update:visible="mobileChartVisible = $event"
        />

        <ChartingMobileDetailDialog
            v-if="viewport.isMobile.value"
            :visible="mobileDetailVisible"
            :stock="mobileDetailVisible ? mobileActiveStock : null"
            @update:visible="mobileDetailVisible = $event"
        />

        <ChartingMobileFavoriteSheet
            v-if="viewport.isMobile.value"
            :visible="mobileFavoriteVisible"
            :stock="mobileActiveStock"
            :favorite-folders="workspace.favoriteFoldersResource.data.value || []"
            :can-use-favorites="workspace.capabilities.value.canUseFavorites"
            :is-mutating="workspace.isMutatingFavorites.value"
            :error="workspace.favoriteMutationError.value"
            @update:visible="mobileFavoriteVisible = $event"
            @toggle-folder="toggleMobileFavoriteFolder"
        />

        <ChartingWorkspaceSearchDialog
            :visible="searchDialogVisible"
            :entry-type="workspace.entry.value.type"
            :entry-value="workspace.entry.value.value"
            :tier-label="workspace.capabilities.value.tierLabel"
            :draft-query="workspace.marketExplorer.state.value && workspace.marketExplorer.state.value.draftQuery"
            :applied-summary="workspace.marketExplorer.summary.value"
            :selection-options="workspace.marketExplorer.selectionOptions"
            :restricted-selections="workspace.marketExplorer.restrictedSelections.value"
            :can-use-saved-searches="workspace.capabilities.value.canUseSavedSearches"
            :can-manage-saved-searches="workspace.capabilities.value.canManageSavedSearches"
            :saved-searches="workspace.savedSearchesResource.data.value || []"
            :can-save-current-search="canSaveCurrentSearch"
            :can-use-description-search="workspace.capabilities.value.canUseDescriptionSearch"
            :can-use-filters="workspace.capabilities.value.canUseFilters"
            :mutation-error="finderControls.mutationError.value"
            :is-ready="finderControls.isReady.value"
            :filters="finderControls.filters.value"
            :options="finderControls.options.value"
            :filter-access="finderControls.filterAccess.value"
            :is-applying="finderControls.isApplying.value"
            :has-pending-changes="finderControls.hasPendingChanges.value"
            @update:visible="handleExplorerVisibility"
            @begin-draft="finderControls.beginDraft"
            @cancel-draft="finderControls.cancelDraft"
            @clear-draft="finderControls.clearDraft"
            @toggle-selection="workspace.marketExplorer.toggleSelection"
            @update-description="workspace.marketExplorer.setDescription"
            @apply-query="handleApplyExplorer"
            @open-source="handleOpenSavedSearch"
            @create-saved-search="handleCreateSavedSearch"
            @rename-saved-search="handleRenameSavedSearch"
            @remove-saved-search="handleRemoveSavedSearch"
            @update:filters="finderControls.filters.value = $event"
        />

        <ChartingWorkspaceWorkspacesDialog
            :visible="workspacesDialogVisible"
            :entry-type="workspace.entry.value.type"
            :entry-value="workspace.entry.value.value"
            :favorite-folders="workspace.favoriteFoldersResource.data.value || []"
            :selected-folder-names="(workspace.selectedStock.value && workspace.selectedStock.value.folderNames) || []"
            :selected-ticker="(workspace.selectedStock.value && workspace.selectedStock.value.ticker) || ''"
            :selected-company-name="(workspace.selectedStock.value && workspace.selectedStock.value.companyName) || ''"
            :can-use-favorites="workspace.capabilities.value.canUseFavorites"
            :can-manage-favorite-folders="workspace.capabilities.value.canManageFavoriteFolders"
            :legacy-href="workspace.legacySourceHref.value"
            @update:visible="workspacesDialogVisible = $event"
            @open-source="workspace.openSource"
            @toggle-favorite-folder="workspace.toggleFavoriteFolder"
            @create-favorite-folder="handleCreateFavoriteFolder"
            @rename-favorite-folder="handleRenameFavoriteFolder"
            @remove-favorite-folder="handleRemoveFavoriteFolder"
        />
    </div>
</template>

<style scoped>
.charting-mobile-workspace { min-width: 0; min-height: calc(100dvh - var(--modern-shell-header-height, 62px)); }
.charting-mobile-workspace__table { min-width: 0; padding: 0.75rem 0.7rem calc(5.5rem + env(safe-area-inset-bottom)); }
.charting-mobile-workspace__table ::v-deep .charting-b-table-stage { height: auto; overflow: visible; }
.charting-mobile-workspace__table ::v-deep .charting-b-table-stage__frame { overflow-x: auto; overflow-y: visible; }
.charting-mobile-workspace__table ::v-deep .charting-b-table-stage__scroll { height: auto; overflow-x: auto; overflow-y: visible; }
.charting-mobile-workspace__table ::v-deep .charting-b-table-stage__ticker-column { width: 11rem; min-width: 11rem; max-width: 11rem; }
</style>
